import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "react-toastify";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import { Tag } from "primereact/tag";
import { Password } from "primereact/password";

import { editUser, getUserById, changeUserPassword } from "../../api/userApi";
import PersonalDetails from "./PersonalDetails";
import StudentDetails from "./StudentDetails";
import TeacherDetails from "./TeacherDetails";

const UserProfile = () => {
  const { user } = useAuth();
  const roleName = user?.roleName || user?.role?.name || user?.Role?.name;
  const [loading, setLoading] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [userDetails, setUserDetails] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    full_name: "",
    grade: "",
    gender: "",
    birth_date: null,
    parent_id: null,
    profileFile: null,
  });

  useEffect(() => {
    if (user?.id) {
      getUserById(user.id)
        .then((data) => setUserDetails(data))
        .catch(() => toast.error("Failed to load profile"));
    }
  }, [user?.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUserDetails((prev) => ({
        ...prev,
        profileFile: file,
        profileImage: { filePath: URL.createObjectURL(file) },
      }));
    }
  };

  const handleDeleteImage = () => {
    setUserDetails((prev) => ({ ...prev, profileImage: null, deleteProfileImage: true }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await editUser(user.id, userDetails);
      if (response.success) {
        toast.success(response.message || "Profile updated", { autoClose: 2000 });
      } else {
        toast.error(response.message || "Update failed", { autoClose: 2000 });
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.msg || "Failed to update profile";
      toast.error(msg, { autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.warn("Password must be at least 6 characters", { autoClose: 3000 });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.warn("Passwords do not match", { autoClose: 3000 });
      return;
    }
    setPwLoading(true);
    try {
      const response = await changeUserPassword(user.id, newPassword);
      if (response.success) {
        toast.success(response.msg || "Password changed successfully", { autoClose: 2000 });
        setNewPassword("");
        setConfirmPassword("");
      } else {
        toast.error(response.msg || "Failed to change password", { autoClose: 3000 });
      }
    } catch (err) {
      const msg = err?.response?.data?.msg || "Failed to change password";
      toast.error(msg, { autoClose: 3000 });
    } finally {
      setPwLoading(false);
    }
  };

  const hasRoleDetails = roleName === "Student" || roleName === "Teacher";

  const getRoleSeverity = (role) => {
    const map = { SuperAdmin: "danger", Admin: "warning", Teacher: "info", Parent: "success", Student: null };
    return map[role] || null;
  };

  return (
    <div className="p-3 md:p-4" style={{ maxWidth: "1100px", margin: "0 auto" }}>
      {/* Header */}
      <div className="flex align-items-center justify-content-between mb-4">
        <div>
          <h2 className="text-2xl font-bold m-0">My Profile</h2>
          <p className="text-color-secondary text-sm mt-1 mb-0">Manage your account details and preferences</p>
        </div>
        <Tag value={roleName} severity={getRoleSeverity(roleName)} className="text-sm" />
      </div>

      <div className="grid">
        {/* Personal Details & Contact */}
        <div className={`col-12 ${hasRoleDetails ? "lg:col-6" : ""}`}>
          <Card className="shadow-2">
            <PersonalDetails
              userDetails={userDetails}
              setUserDetails={setUserDetails}
              handleChange={handleChange}
              handleFileChange={handleFileChange}
              handleDeleteImage={handleDeleteImage}
            />
            <div className="flex justify-content-end mt-3">
              <Button
                label="Save Profile"
                icon="pi pi-check"
                onClick={handleSubmit}
                loading={loading}
                className="px-5"
              />
            </div>
          </Card>
        </div>

        {/* Role-specific Details */}
        {roleName === "Student" && (
          <div className="col-12 lg:col-6">
            <Card className="h-full shadow-2">
              <div className="flex align-items-center gap-2 mb-3">
                <i className="pi pi-graduation-cap text-xl text-primary" />
                <h3 className="text-lg font-semibold m-0">Student Details</h3>
              </div>
              <Divider className="mt-0 mb-3" />
              <StudentDetails user={userDetails} setUser={setUserDetails} />
            </Card>
          </div>
        )}

        {roleName === "Teacher" && (
          <div className="col-12 lg:col-6">
            <Card className="h-full shadow-2">
              <div className="flex align-items-center gap-2 mb-3">
                <i className="pi pi-book text-xl text-primary" />
                <h3 className="text-lg font-semibold m-0">Teacher Details</h3>
              </div>
              <Divider className="mt-0 mb-3" />
              <TeacherDetails user={userDetails} setUser={setUserDetails} />
            </Card>
          </div>
        )}

        {/* Change Password — Separate Card */}
        <div className={`col-12 ${hasRoleDetails ? "lg:col-6" : "lg:col-6"}`}>
          <Card className="shadow-2">
            <div className="flex align-items-center gap-2 mb-3">
              <i className="pi pi-lock text-xl text-primary" />
              <h3 className="text-lg font-semibold m-0">Change Password</h3>
            </div>
            <Divider className="mt-0 mb-3" />
            <div className="grid">
              <div className="col-12">
                <label htmlFor="newPassword" className="block text-sm font-medium text-color-secondary mb-1">New Password</label>
                <Password
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  toggleMask
                  feedback={false}
                  className="w-full"
                  inputClassName="w-full"
                />
              </div>
              <div className="col-12">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-color-secondary mb-1">Confirm Password</label>
                <Password
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  toggleMask
                  feedback={false}
                  className="w-full"
                  inputClassName="w-full"
                />
              </div>
            </div>
            <div className="flex justify-content-end mt-3">
              <Button
                label="Update Password"
                icon="pi pi-lock"
                onClick={handlePasswordChange}
                loading={pwLoading}
                severity="warning"
                className="px-4"
                disabled={!newPassword || !confirmPassword}
              />
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
